import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { CompanyService } from 'app/services/company.service';
import { CompanyModel } from 'app/models/company.model';
import { ApiService } from 'app/shared/apis/api.service';
import { AutoTranslatePipe } from 'app/shared/pipes/translate.pipe';
import { BehaviorSubject, Observable, map, switchMap, take } from 'rxjs';

@Component({
    selector: 'company-selector',
    standalone: true,
    imports: [CommonModule, MatButtonModule, MatIconModule, MatMenuModule, AutoTranslatePipe],
    template: `
        <ng-container *ngIf="companies$ | async as companies">
            <div *ngIf="companies.length > (requesterMode ? 0 : 1)" class="flex justify-center px-3 pb-10">
                <button
                    type="button"
                    class="company-badge"
                    [matMenuTriggerFor]="companyMenu"
                    [attr.aria-label]="(requesterMode ? 'Alterar solicitante' : 'Alterar empresa') | autoTranslate"
                >
                    <span class="company-name" dir="auto">{{ currentCompanyName$ | async }}</span>
                    <mat-icon class="icon-size-3.5" [svgIcon]="'heroicons_outline:chevron-down'"></mat-icon>
                </button>
            </div>

            <mat-menu #companyMenu="matMenu" panelClass="company-menu-panel">
                <button
                    mat-menu-item
                    *ngFor="let company of companies"
                    (click)="onCompanyChange(company.company_id)"
                    class="company-option"
                >
                    <img
                        *ngIf="company.logo; else logoPlaceholder"
                        [src]="company.logo"
                        [alt]="company.name"
                        class="company-option-logo"
                    />
                    <ng-template #logoPlaceholder>
                        <mat-icon class="company-option-logo icon-size-4" [svgIcon]="'heroicons_outline:building-office-2'"></mat-icon>
                    </ng-template>
                    <span class="company-option-text">
                        <span class="company-name" dir="auto">{{ company.name }}</span>
                        <span *ngIf="companyLocation(company) as location" class="company-location" dir="auto">{{ location }}</span>
                    </span>
                </button>
            </mat-menu>
        </ng-container>
    `,
    styles: [
        `
            .company-badge {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                max-width: 100%;
                padding: 2px 10px;
                border-radius: 999px;
                border: none;
                background-color: rgba(0, 0, 0, 0.05);
                cursor: pointer;
            }

            :host-context(.dark) .company-badge {
                background-color: rgba(255, 255, 255, 0.08);
            }

            .company-badge .company-name {
                font-size: 11px;
                font-weight: 500;
            }

            .company-name {
                display: block;
                overflow: hidden;
                text-align: start;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            ::ng-deep .company-menu-panel {
                min-width: 280px !important;
                max-width: 420px !important;
            }

            .company-option {
                display: flex;
                align-items: center;
                gap: 8px;
                height: auto;
                min-height: 48px;
                padding-top: 6px;
                padding-bottom: 6px;
                width: 100%;
            }

            .company-option-logo {
                flex-shrink: 0;
                width: 20px;
                height: 20px;
                margin-right: 8px;
                object-fit: contain;
                border-radius: 4px;
            }

            .company-option-text {
                display: flex;
                flex-direction: column;
                overflow: hidden;
                line-height: 1.25;
                white-space: normal;
            }

            .company-option-text .company-name {
                white-space: normal;
                overflow: visible;
                text-overflow: unset;
            }

            ::ng-deep .mat-mdc-menu-item .company-location,
            ::ng-deep .mat-mdc-menu-item span.company-location {
                display: block;
                overflow: hidden;
                text-align: start;
                text-overflow: ellipsis;
                white-space: nowrap;
                font-size: 9px !important;
                font-weight: 400 !important;
                opacity: 0.55;
                margin-top: 1px;
                line-height: 1.3 !important;
            }
        `,
    ],
})
export class CompanySelectorComponent implements OnInit, OnChanges {
    @Input() requesterMode = false;

    private _companies = new BehaviorSubject<CompanyModel[]>([]);
    companies$: Observable<CompanyModel[]> = this._companies.asObservable();
    currentCompanyId$: Observable<number | null> = this.companyService.currentCompany$.pipe(
        map((company) => (company ? company.company_id : null)),
    );
    currentCompanyName$: Observable<string> = this.companyService.currentCompany$.pipe(
        map((company) => company?.name ?? ''),
    );

    constructor(private companyService: CompanyService) {}

    ngOnInit(): void {
        this.loadCompanies();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['requesterMode'] && !changes['requesterMode'].firstChange) {
            this.loadCompanies();
        }
    }

    private loadCompanies(): void {
        if (this.requesterMode) {
            ApiService.userCurrent
                .pipe(
                    take(1),
                    switchMap((user) =>
                        user?.is_admin
                            ? this.companyService.listAllRequesterCompanies()
                            : this.companyService.listMyRequesterCompanies(),
                    ),
                )
                .subscribe((items) => {
                    this._companies.next(items || []);
                    this.ensureCompanySelected(items);
                });
            return;
        }

        this.companyService.listAppTenantCompanies().subscribe((items) => {
            this._companies.next(items || []);
            this.ensureCompanySelected(items || []);
        });
    }

    /** Pré-seleciona a primeira empresa da lista quando ainda não há empresa corrente definida. */
    private ensureCompanySelected(items: CompanyModel[]): void {
        if (this.companyService.getCurrentCompany() || !items?.length) {
            return;
        }
        this.companyService.setCurrentCompany(items[0]);
    }

    onCompanyChange(companyId: number): void {
        const company = this._companies.value.find((c) => c.company_id === companyId);
        if (company) {
            this.companyService.setCurrentCompany(company);
        }
    }

    companyLocation(company: CompanyModel): string {
        const cityUf = [company.city, company.UF].filter(Boolean).join('/');
        return [cityUf, company.country].filter(Boolean).join(' - ');
    }
}
